import React, { memo, useEffect, useState, useCallback } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { getQuickBooksConfig, saveQuickBooksConfig, extractQbError } from '../actions';
import type {
  QuickBooksConfig as ConfigType,
  QuickBooksConfigProps,
  QuickBooksItem,
  Pipeline,
  PipelineStage,
} from '../types';

import classes from './quickBooksConfig.module.css';

function QuickBooksConfig({ locationId, ssoToken, onConfigSaved }: QuickBooksConfigProps) {
  const [config, setConfig] = useState<ConfigType | null>(null);
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [items, setItems] = useState<QuickBooksItem[]>([]);
  const [selectedPipeline, setSelectedPipeline] = useState<string>('');
  const [invoiceStage, setInvoiceStage] = useState<string>('');
  const [paidStage, setPaidStage] = useState<string>('');
  const [defaultItemId, setDefaultItemId] = useState<string>('');
  const [syncContactsOnly, setSyncContactsOnly] = useState<boolean>(false);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // The backend returns the persisted config plus the pipelines/items needed
      // to populate the form in a single call.
      const { config: configData, pipelines: pipelinesData, items: itemsData } =
        await getQuickBooksConfig(locationId, ssoToken);

      setPipelines(pipelinesData);
      setItems(itemsData);
      setConfig(configData);

      // Pre-populate the form. The backend stores GHL pipeline/stage *IDs* under
      // pipelineId/invoiceStageId/paidStageId, so match dropdown options by ID.
      if (configData) {
        const matchingPipeline = pipelinesData.find((p) => p.id === configData.pipelineId);
        if (matchingPipeline) {
          setSelectedPipeline(matchingPipeline.id);
          setStages(matchingPipeline.stages);
        }
        setInvoiceStage(configData.invoiceStageId || '');
        setPaidStage(configData.paidStageId || '');
        setDefaultItemId(configData.defaultItemId || '');
        // `syncContactsOnly` is the inverse of the backend `autoCreateInvoice`.
        setSyncContactsOnly(!(configData.autoCreateInvoice ?? true));
      }
    } catch (err) {
      setError(extractQbError(err, 'Failed to load configuration'));
      // eslint-disable-next-line no-console
      console.error('Error fetching QuickBooks config:', err);
    } finally {
      setLoading(false);
    }
  }, [locationId, ssoToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePipelineChange = useCallback(
    (pipelineId: string) => {
      setSelectedPipeline(pipelineId);
      setInvoiceStage('');
      setPaidStage('');

      const pipeline = pipelines.find((p) => p.id === pipelineId);
      setStages(pipeline?.stages || []);
    },
    [pipelines]
  );

  // Invoice creation needs a default item; only required when invoices will be created.
  const requiresDefaultItem = !syncContactsOnly;

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setSuccess(null);

      // Paid stage is optional (valid "create invoices, don't auto-move to paid" setup).
      if (!selectedPipeline || !invoiceStage) {
        setError('Select a pipeline and an invoice creation stage.');
        return;
      }
      if (requiresDefaultItem && !defaultItemId) {
        setError('Select a default item — it is required to create invoices in QuickBooks.');
        return;
      }

      try {
        setSaving(true);
        // Send GHL IDs (not names) — the backend matches opportunities by
        // pipeline/stage ID. Location is derived from the SSO context.
        await saveQuickBooksConfig(
          {
            pipelineId: selectedPipeline,
            invoiceStageId: invoiceStage,
            paidStageId: paidStage || undefined,
            defaultItemId: defaultItemId || undefined,
            autoCreateInvoice: !syncContactsOnly,
          },
          ssoToken
        );
        setSuccess('Configuration saved successfully!');
        onConfigSaved?.();

        // Clear success after 3 seconds
        setTimeout(() => setSuccess(null), 3000);
      } catch (err) {
        setError(extractQbError(err, 'Failed to save configuration'));
        // eslint-disable-next-line no-console
        console.error('Error saving QuickBooks config:', err);
      } finally {
        setSaving(false);
      }
    },
    [
      ssoToken,
      selectedPipeline,
      invoiceStage,
      paidStage,
      defaultItemId,
      requiresDefaultItem,
      syncContactsOnly,
      onConfigSaved,
    ]
  );

  // Resolve stored IDs to friendly names for the "Current Configuration" summary.
  const configPipeline = config ? pipelines.find((p) => p.id === config.pipelineId) : undefined;
  const configPipelineName = configPipeline?.name || config?.pipelineId || '—';
  const configInvoiceStageName =
    configPipeline?.stages.find((s) => s.id === config?.invoiceStageId)?.name ||
    config?.invoiceStageId ||
    '—';
  const configPaidStageName =
    configPipeline?.stages.find((s) => s.id === config?.paidStageId)?.name ||
    config?.paidStageId ||
    'Not set';
  const configItemName = items.find((i) => i.id === config?.defaultItemId)?.name || config?.defaultItemId || '—';

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2 text-muted">Loading configuration...</p>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <h3 className="mb-4">QuickBooks Configuration</h3>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success" role="alert">
          {success}
        </div>
      )}

      {config && (
        <div className={classes.currentConfig}>
          <h5>Current Configuration</h5>
          <p className="mb-1">
            <strong>Pipeline:</strong>
{' '}
{configPipelineName}
          </p>
          <p className="mb-1">
            <strong>Invoice Creation Stage:</strong>
{' '}
{configInvoiceStageName}
          </p>
          <p className="mb-1">
            <strong>Payment Complete Stage:</strong>
{' '}
{configPaidStageName}
          </p>
          <p className="mb-1">
            <strong>Default Item:</strong>
{' '}
{configItemName}
          </p>
          <p className="mb-0">
            <strong>Mode:</strong>
{' '}
            {config.autoCreateInvoice ? 'Full Sync (Customers + Invoices)' : 'Sync Contacts Only'}
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label htmlFor="pipeline" className="form-label">
            Select Pipeline
{' '}
<span className="text-danger">*</span>
          </label>
          <select
            id="pipeline"
            className="form-select"
            value={selectedPipeline}
            onChange={(e) => handlePipelineChange(e.target.value)}
            required
          >
            <option value="">Select a pipeline...</option>
            {pipelines.map((pipeline) => (
              <option key={pipeline.id} value={pipeline.id}>
                {pipeline.name}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-3">
          <label htmlFor="invoiceStage" className="form-label">
            Invoice Creation Stage
{' '}
<span className="text-danger">*</span>
          </label>
          <select
            id="invoiceStage"
            className="form-select"
            value={invoiceStage}
            onChange={(e) => setInvoiceStage(e.target.value)}
            disabled={!selectedPipeline}
            required
          >
            <option value="">Select a pipeline first</option>
            {stages.map((stage) => (
              <option key={stage.id} value={stage.id}>
                {stage.name}
              </option>
            ))}
          </select>
          <div className="form-text">
            When opportunities reach this stage, invoices will be created in QuickBooks.
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="paidStage" className="form-label">
            Payment Complete Stage
{' '}
<span className="text-muted">(optional)</span>
          </label>
          <select
            id="paidStage"
            className="form-select"
            value={paidStage}
            onChange={(e) => setPaidStage(e.target.value)}
            disabled={!selectedPipeline}
          >
            <option value="">None — don&apos;t move on payment</option>
            {stages.map((stage) => (
              <option key={stage.id} value={stage.id}>
                {stage.name}
              </option>
            ))}
          </select>
          <div className="form-text">
            When invoices are paid in QuickBooks, opportunities will move to this stage. Leave as
            None to keep invoices flowing without auto-moving the opportunity.
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="defaultItem" className="form-label">
            Default Item
{' '}
{requiresDefaultItem && <span className="text-danger">*</span>}
          </label>
          <select
            id="defaultItem"
            className="form-select"
            value={defaultItemId}
            onChange={(e) => setDefaultItemId(e.target.value)}
          >
            <option value="">Select a QuickBooks item...</option>
            {items.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
          <div className="form-text">
            The QuickBooks product/service line item used when creating invoices. Required when
            invoices are being created.
          </div>
        </div>

        <div className="mb-4">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="syncContactsOnly"
              checked={syncContactsOnly}
              onChange={(e) => setSyncContactsOnly(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="syncContactsOnly">
              Sync contacts only (don&apos;t create invoices)
            </label>
          </div>
          <div className="form-text">
            When enabled, only customers will be created in QuickBooks - no invoices will be generated.
          </div>
        </div>

        <button
          type="submit"
          className="btn btn-primary"
          disabled={saving || !selectedPipeline || !invoiceStage}
        >
          {saving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Saving...
            </>
          ) : (
            'Save Configuration'
          )}
        </button>
      </form>
    </div>
  );
}

const QuickBooksConfigMemo = memo(QuickBooksConfig, areEqual);

export { QuickBooksConfigMemo as QuickBooksConfig };
